import React from 'react';
import { Task, User } from '../../types';
import { Calendar, Clock, User as UserIcon, Flag, CheckSquare, AlertTriangle, FileText } from 'lucide-react';

interface DocViewProps {
  tasks: Task[];
  users: User[];
  project: any;
  onSelectTask?: (task: Task) => void;
}

const DocView: React.FC<DocViewProps> = ({ tasks, users, project, onSelectTask }) => {
  const generateTaskId = (taskId: number) => {
    return `TSK-${String(taskId).padStart(5, '0')}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-700 bg-green-50 border-green-200';
      case 'in_progress': return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'review': return 'text-purple-700 bg-purple-50 border-purple-200';
      case 'todo': return 'text-gray-700 bg-gray-50 border-gray-200';
      default: return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      case 'review': return 'Review';
      case 'todo': return 'To Do';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-700 bg-red-50 border-red-200';
      case 'high': return 'text-orange-700 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-gray-700 bg-gray-50 border-gray-200';
      default: return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  // Group tasks by status for better organization
  const tasksByStatus = {
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    todo: tasks.filter(t => t.status === 'todo'),
    review: tasks.filter(t => t.status === 'review'),
    completed: tasks.filter(t => t.status === 'completed'),
  };

  const renderTaskSection = (task: Task, index: number) => {
    const assignedUser = users.find(u => u.id === task.assignedTo);

    return (
      <div
        key={task.id}
        className="mb-8 pb-8 border-b border-gray-200 last:border-b-0 cursor-pointer hover:bg-gray-50 -mx-6 px-6 py-4 rounded-lg transition-colors"
        onClick={() => onSelectTask?.(task)}
      >
        {/* Task Header */}
        <div className="mb-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-xl font-semibold text-gray-900">{task.title}</h3>
                <span className="text-sm text-gray-500 font-mono">{generateTaskId(task.id)}</span>
              </div>
            </div>
          </div>

          {/* Task Metadata */}
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-medium border ${getPriorityColor(task.priority || 'medium')}`}>
              <Flag className="w-3.5 h-3.5" />
              Priority: {(task.priority || 'medium').charAt(0).toUpperCase() + (task.priority || 'medium').slice(1)}
            </span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-medium border ${getStatusColor(task.status)}`}>
              <CheckSquare className="w-3.5 h-3.5" />
              Status: {getStatusLabel(task.status)}
            </span>
            {assignedUser && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-medium border text-indigo-700 bg-indigo-50 border-indigo-200">
                <UserIcon className="w-3.5 h-3.5" />
                Assignee: {assignedUser.first_name} {assignedUser.last_name}
              </span>
            )}
            {task.estimatedHours && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-medium border text-purple-700 bg-purple-50 border-purple-200">
                <Clock className="w-3.5 h-3.5" />
                Est: {task.estimatedHours}h
              </span>
            )}
            {task.dueDate && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-medium border text-amber-700 bg-amber-50 border-amber-200">
                <Calendar className="w-3.5 h-3.5" />
                Due: {new Date(task.dueDate).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {/* Task Description */}
        {task.description && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Description</h4>
            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-200">
              {task.description}
            </div>
          </div>
        )}

        {/* Parse Acceptance Criteria if present in description */}
        {task.description && task.description.includes('Acceptance Criteria') && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-2">
              <CheckSquare className="w-4 h-4" />
              Key Points
            </h4>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                {task.description
                  .split('\n')
                  .filter(line => line.trim().startsWith('-'))
                  .map((line, idx) => (
                    <li key={idx}>{line.trim().substring(1).trim()}</li>
                  ))}
              </ul>
            </div>
          </div>
        )}

        {/* Blockers or Special Notes */}
        {task.description && (task.description.toLowerCase().includes('blocked') || task.description.toLowerCase().includes('waiting')) && (
          <div className="mb-4">
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900">
                <span className="font-semibold">Note: </span>
                {task.description
                  .split('\n')
                  .find(line => line.toLowerCase().includes('blocked') || line.toLowerCase().includes('waiting'))}
              </div>
            </div>
          </div>
        )}

        {/* References (URLs in description) */}
        {task.description && (task.description.includes('http://') || task.description.includes('https://')) && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-2">
              <FileText className="w-4 h-4" />
              References
            </h4>
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
              {task.description
                .split('\n')
                .filter(line => line.includes('http://') || line.includes('https://'))
                .map((line, idx) => {
                  const urlMatch = line.match(/(https?:\/\/[^\s]+)/);
                  const url = urlMatch ? urlMatch[1] : '';
                  const label = line.split(':')[0].trim();
                  return (
                    <div key={idx} className="text-sm mb-1">
                      <span className="text-gray-600">{label}: </span>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {url.length > 60 ? url.substring(0, 60) + '...' : url}
                      </a>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Document Header */}
      <div className="border-b border-gray-200 px-6 py-6 bg-gradient-to-r from-blue-50 to-indigo-50">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{project?.name || 'Project Tasks'}</h1>
        <p className="text-sm text-gray-600">{project?.description || ''}</p>
        <div className="flex items-center gap-4 mt-4 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <CheckSquare className="w-4 h-4" />
            {tasks.length} Total Tasks
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {tasks.reduce((sum, t) => sum + (t.estimatedHours || 0), 0)}h Estimated
          </span>
          {project?.client && (
            <>
              <span>•</span>
              <span>Client: {project.client.name}</span>
            </>
          )}
        </div>
      </div>

      {/* Document Content */}
      <div className="px-6 py-6">
        {/* In Progress Tasks */}
        {tasksByStatus.in_progress.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-500 flex items-center gap-2">
              <div className="w-1 h-6 bg-blue-500 rounded"></div>
              In Progress ({tasksByStatus.in_progress.length})
            </h2>
            {tasksByStatus.in_progress.map((task, idx) => renderTaskSection(task, idx))}
          </div>
        )}

        {/* To Do Tasks */}
        {tasksByStatus.todo.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-500 flex items-center gap-2">
              <div className="w-1 h-6 bg-gray-500 rounded"></div>
              To Do ({tasksByStatus.todo.length})
            </h2>
            {tasksByStatus.todo.map((task, idx) => renderTaskSection(task, idx))}
          </div>
        )}

        {/* Review Tasks */}
        {tasksByStatus.review.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-purple-500 flex items-center gap-2">
              <div className="w-1 h-6 bg-purple-500 rounded"></div>
              In Review ({tasksByStatus.review.length})
            </h2>
            {tasksByStatus.review.map((task, idx) => renderTaskSection(task, idx))}
          </div>
        )}

        {/* Completed Tasks */}
        {tasksByStatus.completed.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-green-500 flex items-center gap-2">
              <div className="w-1 h-6 bg-green-500 rounded"></div>
              Completed ({tasksByStatus.completed.length})
            </h2>
            {tasksByStatus.completed.map((task, idx) => renderTaskSection(task, idx))}
          </div>
        )}

        {/* Empty State */}
        {tasks.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No tasks found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first task.
            </p>
          </div>
        )}
      </div>

      {/* Document Footer */}
      <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 text-xs text-gray-500">
        Generated: {new Date().toLocaleDateString()} • {tasks.length} tasks •
        {tasks.filter(t => t.status === 'completed').length} completed •
        {tasks.filter(t => t.status === 'in_progress').length} in progress •
        {tasks.filter(t => t.status === 'todo').length} to do
      </div>
    </div>
  );
};

export default DocView;
